import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CaretRight, Moon, Sun, ArrowsClockwise } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './Button';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { syncProductsFromKameone } from '../services/api';

const CONFIG_TABS = [
  { label: 'Listas de precios', slug: 'listas-de-precios' },
  { label: 'Productos', slug: 'productos' },
  { label: 'Credenciales', slug: 'credenciales' },
  { label: 'Sincronización', slug: 'sincronizacion' },
  { label: 'Vendedores', slug: 'vendedores' },
];

interface MenuItem {
  label: string;
  onClick: () => void;
}

interface MenuOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToOrders?: () => void;
  onNavigateToAllChats?: () => void;
  onNavigateToHome?: () => void;
  onNavigateToClients?: () => void;
  onNavigateToDailyReport?: () => void;
  onNavigateToSettings?: () => void;
  onLogout?: () => void;
}

export const MenuOverlay: React.FC<MenuOverlayProps> = ({
  isOpen,
  onClose,
  onNavigateToOrders,
  onNavigateToAllChats,
  onNavigateToHome,
  onNavigateToClients,
  onNavigateToDailyReport,
  onNavigateToSettings,
  onLogout
}) => {
  const { theme, toggleTheme } = useTheme();
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  const handleSyncProducts = async () => {
    if (isSyncing) return;
    try {
      setIsSyncing(true);
      setSyncMessage('');
      const token = await getAccessToken();
      await syncProductsFromKameone(token);
      setSyncMessage('Productos sincronizados');
      setTimeout(() => setSyncMessage(''), 3000);
    } catch {
      setSyncMessage('Error sincronizando');
      setTimeout(() => setSyncMessage(''), 3000);
    } finally {
      setIsSyncing(false);
    }
  };
  const menuItems: MenuItem[] = [
    {
      label: 'Inicio',
      onClick: () => {
        onNavigateToHome?.();
        onClose();
      }
    },
    {
      label: 'Conversaciones',
      onClick: () => {
        onNavigateToAllChats?.();
        onClose();
      }
    },
    {
      label: 'Configuración',
      onClick: () => {
        onNavigateToSettings?.();
        onClose();
      }
    }
  ];

  const logoutItem: MenuItem = {
    label: 'Cerrar sesión',
    onClick: () => {
      console.log('🔴 Botón "Cerrar sesión" clickeado en MenuOverlay');
      console.log('🔍 onLogout function exists:', !!onLogout);
      onLogout?.();
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/20 z-40"
            onClick={onClose}
          />
          
          {/* Menu Overlay */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="fixed inset-y-0 right-0 bg-muted w-full max-w-md z-50 flex flex-col"
            data-name="menu"
            data-node-id="17:831"
          >
            {/* Header */}
            <div
              className="bg-muted flex items-center justify-between px-3 py-2 border-b border-border"
              data-name="header"
              data-node-id="17:953"
            >
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  className="hover:bg-accent/5"
                  aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                >
                  {theme === 'light' ? (
                    <Moon size={20} weight="bold" />
                  ) : (
                    <Sun size={20} weight="bold" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSyncProducts}
                  disabled={isSyncing}
                  className="hover:bg-accent/5"
                  aria-label="Actualizar productos con Kame"
                >
                  <ArrowsClockwise size={20} weight="bold" className={isSyncing ? 'animate-spin' : ''} />
                </Button>
              </div>

              <div className="flex-1 text-center">
                {syncMessage && (
                  <span className="font-sans text-xs text-text-subtle">{syncMessage}</span>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="hover:bg-accent/5"
                aria-label="Cerrar menú"
              >
                <X size={20} weight="bold" />
              </Button>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto px-3 py-2">
              <div className="flex flex-col gap-0">
                {menuItems.map((item, index) => (
                  <div
                    key={index}
                    className="border-b border-border flex flex-col items-start px-0 py-1"
                    data-name="tile"
                  >
                    <Button
                      variant="ghost"
                      size="md"
                      onClick={item.onClick}
                      className="w-full !justify-between hover:bg-accent/5 rounded-lg"
                      rightIcon={<CaretRight size={16} weight="bold" />}
                    >
                      {item.label}
                    </Button>
                    {item.label === 'Configuración' && (
                      <div className="flex flex-col w-full pl-4 pb-1">
                        {CONFIG_TABS.map(tab => (
                          <button
                            key={tab.slug}
                            type="button"
                            onClick={() => { navigate(`/configuracion/${tab.slug}`); onClose(); }}
                            className="text-left text-sm font-sans text-text-subtle hover:text-text px-3 py-1.5 rounded-md hover:bg-accent/5 transition-colors"
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Logout Item - Separated and Styled */}
            <div className="border-t border-border px-3 py-2 mt-auto">
              <div className="bg-black/10 rounded-lg">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logoutItem.onClick}
                  className="w-full text-xs text-text-subtle hover:text-text rounded-lg"
                >
                  {logoutItem.label}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MenuOverlay;

